import { useNavigate, useSearchParams } from 'react-router-dom'
import { CardGrid, ModuleCard, Page } from '@rammetec/design-system'
import { Boxes } from 'lucide-react'
import { useAvailableModules } from '@syscolect/application'
import type { ModuleInstance } from '@syscolect/domain'
import type { AppModule } from './registry'
import { persistMid } from './modulePreferences'

export interface InstancePickerProps {
  slug: string
  candidates: readonly ModuleInstance[]
}

/**
 * Disambiguation surface — rendered by `ModuleEntry` when the user has multiple
 * instances of the same slug and no valid `mid` is present. Clicking a card
 * persists the choice and adds `?mid=<chosen>` (preserving other query params).
 */
export function InstancePicker({ slug, candidates }: InstancePickerProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const modules = useAvailableModules<AppModule>()
  const module = modules.find((m) => m.slug === slug)

  const handlePick = (instanceId: string) => {
    persistMid(slug, instanceId)
    const next = new URLSearchParams(searchParams)
    next.set('mid', instanceId)
    navigate({ pathname: `/m/${slug}`, search: next.toString() }, { replace: true })
  }

  return (
    <Page
      name={`console.module.${slug}.picker`}
      title={module?.label ?? slug}
      description={`Você tem ${candidates.length} configurações deste módulo. Escolha qual abrir — esta seleção fica embutida no link.`}
      productArea="console"
    >
      <CardGrid columns={{ sm: 2, lg: 3 }}>
        {candidates.map((instance) => (
          <ModuleCard
            key={instance.id}
            icon={module?.iconNode ?? <Boxes />}
            title={instance.label ?? module?.label ?? slug}
            description={summarizeParameters(instance.parameters)}
            aria-label={`Abrir ${instance.label ?? slug}`}
            onClick={() => handlePick(instance.id)}
          />
        ))}
      </CardGrid>
    </Page>
  )
}

function summarizeParameters(params: Record<string, unknown>): string {
  const entries = Object.entries(params)
  if (entries.length === 0) return 'Sem parâmetros configurados.'
  return entries.map(([key, value]) => `${key}: ${formatValue(value)}`).join(' · ')
}

function formatValue(value: unknown): string {
  if (value == null) return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}
